import { PlaySquare, Video } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseRecord, BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorLoading,
  SelectorResultItem,
  SelectorResults,
  SelectorSearch,
} from "../Selector";
import { useZoomListRecordings } from "./useZoomListRecordings";

type ObjectType = "meetings" | "recordings";

interface ObjectTypeConfig {
  label: string;
  icon: React.ReactNode;
  formatResult: (result: any) => BaseRecord;
}

export const ZoomSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Configure how each object type should be displayed and formatted
  const objectTypes: Record<ObjectType, ObjectTypeConfig> = {
    meetings: {
      label: "Meetings",
      icon: <Video className="h-4 w-4" />,
      formatResult: (meeting) => ({
        id: meeting.id.toString(),
        title: meeting.topic || "Untitled Meeting",
        subtitle: `${new Date(meeting.start_time).toLocaleString()} (${meeting.duration} min)`,
      }),
    },
    recordings: {
      label: "Recordings",
      icon: <PlaySquare className="h-4 w-4" />,
      formatResult: (recording) => ({
        id: recording.uuid,
        title: recording.topic || "Untitled Recording",
        subtitle: `${new Date(recording.start_time).toLocaleString()} (${recording.duration} min)`,
      }),
    },
  };

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useZoomListRecordings({ connection, dateRange });

  // Flatten pages into single array
  const allRecordings = data?.pages?.flatMap((page) => page.data) ?? [];

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed recording.
  const genericById = useMemo(
    () =>
      buildGenericById(
        data?.pages?.flatMap((page) => toGenericArray(page.data_generic)) ?? [],
      ),
    [data],
  );

  // Client-side search filter
  const filteredRecordings = allRecordings.filter((recording) =>
    recording.data.topic.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Scroll detection
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const bottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    if (bottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Manual load earlier function
  const handleLoadEarlier = () => {
    // Calculate new date range: extend backwards by 3 months
    const currentFrom = dateRange?.from
      ? new Date(dateRange.from)
      : new Date(new Date().setDate(new Date().getDate() - 30));

    const newFrom = new Date(currentFrom);
    newFrom.setMonth(newFrom.getMonth() - 3);

    const newTo = new Date(currentFrom);
    newTo.setDate(newTo.getDate() - 1); // End one day before current range starts

    setDateRange({
      from: newFrom.toISOString().split("T")[0],
      to: newTo.toISOString().split("T")[0],
    });
  };

  const handleRecordSelect = (recording: any) => {
    onSelect(recording);
  };

  const recordingIds = useMemo(
    () =>
      filteredRecordings.map(
        (r: any) => objectTypes.recordings.formatResult(r.data).id,
      ),
    [filteredRecordings],
  );

  return (
    <Selector>
      <SelectorSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search recordings..."
      />
      <SelectorResults onScroll={handleScroll} ref={scrollRef}>
        {isLoading ? (
          <SelectorLoading />
        ) : (
          <>
            {!filteredRecordings.length && !dateRange ? (
              <div className="flex flex-col items-center justify-center p-8 space-y-4">
                <PlaySquare className="h-12 w-12 text-neutral-400" />
                <div className="text-center">
                  <p className="text-sm text-neutral-500 mb-4">
                    No recordings found in the last 30 days
                  </p>
                  <button
                    onClick={handleLoadEarlier}
                    className="text-sm text-teal hover:text-teal font-medium transition-colors"
                  >
                    Search Earlier Recordings
                  </button>
                </div>
              </div>
            ) : !filteredRecordings.length ? (
              <div className="flex flex-col items-center justify-center p-8 space-y-4">
                <PlaySquare className="h-12 w-12 text-neutral-400" />
                <div className="text-center">
                  <p className="text-sm text-neutral-500 mb-4">
                    No recordings found in this date range
                  </p>
                  <button
                    onClick={handleLoadEarlier}
                    className="text-sm text-teal hover:text-teal font-medium transition-colors"
                  >
                    Search Earlier Recordings
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="divide-y">
                  {filteredRecordings.map((recording: any) => {
                    const formatted = objectTypes.recordings.formatResult(
                      recording.data,
                    );
                    return (
                      <EntityDragRow
                        key={formatted.id}
                        entity={recording}
                        generic={genericById.get(recording.data.id)}
                        entityId={formatted.id}
                        orderedIds={recordingIds}
                        onSelect={handleRecordSelect}
                        dragScope="zoom"
                      >
                        <SelectorResultItem
                          icon={objectTypes.recordings.icon}
                          title={formatted.title}
                          subtitle={formatted.subtitle}
                        />
                      </EntityDragRow>
                    );
                  })}
                </div>

                {/* Loading more indicator */}
                {isFetchingNextPage && (
                  <div className="p-4 text-center text-sm text-neutral-500">
                    Loading more...
                  </div>
                )}
              </>
            )}
          </>
        )}
      </SelectorResults>
    </Selector>
  );
};
